import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import { apiGet, apiDelete } from '../../utils/apiFetch'
import apiPath from '../../utils/pathObj'

// Console tracing. Remove (or set to false) after testing.
const DEBUG = true
const log = (...args) => DEBUG && console.log('[Pipedrive UI]', ...args)
const logErr = (...args) => DEBUG && console.error('[Pipedrive UI]', ...args)

const CHANNEL = 'pipedrive_oauth' // same-origin tab-to-tab messaging
const POPUP_NAME = 'pipedrive_oauth_popup'
const POLL_MS = 2000
const MAX_WAIT_MS = 10 * 60 * 1000 // matches the 10 minute `state` expiry on the server

const ERROR_KEYS = {
  access_denied: 'PIPEDRIVE_ERR_ACCESS_DENIED',
  missing_params: 'PIPEDRIVE_ERR_MISSING_PARAMS',
  invalid_state: 'PIPEDRIVE_ERR_INVALID_STATE',
  exchange_failed: 'PIPEDRIVE_ERR_EXCHANGE_FAILED',
}

export default function PipedriveConnection() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // 👇 CHANGED: also pull refreshPipedriveStatus from AuthContext
  const { updatePageName, refreshPipedriveStatus } = useContext(AuthContext)

  const [params] = useSearchParams()

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [waiting, setWaiting] = useState(false) // popup is open, waiting for the user
  const [showCloseHint, setShowCloseHint] = useState(false) // popup page could not close itself
  const [banner, setBanner] = useState(null) // { type: 'success' | 'error' | 'info', text }

  const popupRef = useRef(null)
  const pollRef = useRef(null)
  const watchingRef = useRef(false)
  const finishRef = useRef(() => {})
  const applyResultRef = useRef(() => {})

  // ---------- status ----------
  const load = useCallback(async () => {
    try {
      setLoading(true)
      log('loading status from', apiPath.pipedriveStatus)
      const result = await apiGet(apiPath.pipedriveStatus)
      log('status response', {
        httpStatus: result?.status,
        success: result?.data?.success,
        data: result?.data?.data,
      })
      if (result?.status === 200 && result?.data?.success) {
        setStatus(result.data.data)

        // 👇 CHANGED: keep the global sidebar state in sync with the server
        refreshPipedriveStatus()

        return result.data.data
      }
      logErr('status response not in the expected shape', result)
      setBanner({ type: 'error', text: t('PIPEDRIVE_LOAD_FAILED') })
      return null
    } catch (e) {
      logErr('status request threw', e?.response?.status, e?.response?.data || e?.message)
      setBanner({ type: 'error', text: t('PIPEDRIVE_LOAD_FAILED') })
      return null
    } finally {
      setLoading(false)
    }
  }, [t, refreshPipedriveStatus]) // 👈 CHANGED: added dependency

  // ---------- popup helpers ----------
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // NOTE: do not check popup.closed here. After the popup navigates to Pipedrive the browser
  // reports it as "closed" even though it is still open, so we always just try to close it.
  const closePopup = useCallback(() => {
    const win = popupRef.current
    popupRef.current = null
    if (!win) return
    const tryClose = () => {
      try {
        win.close()
      } catch (e) {
        /* ignore cross-origin */
      }
    }
    tryClose()
    // Retry — some browsers ignore the first close after cross-origin redirects
    setTimeout(tryClose, 100)
    setTimeout(tryClose, 400)
    setTimeout(tryClose, 1000)
  }, [])

  // Shows the real outcome and refreshes the status from the server
  const applyResult = useCallback(
    async (outcome, reason) => {
      if (outcome === 'connected') {
        setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
        await load()
      } else if (outcome === 'error') {
        const key = ERROR_KEYS[reason]
        setBanner({ type: 'error', text: key ? t(key) : t('PIPEDRIVE_CONNECT_FAILED') })
        await load()
      } else {
        // cancelled / timed out: check what actually happened on the server
        const data = await load()
        if (data?.connected) {
          setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
        } else if (data?.status === 'error') {
          setBanner({ type: 'error', text: t('PIPEDRIVE_CONNECT_FAILED') })
        } else {
          setBanner({ type: 'info', text: t('PIPEDRIVE_POPUP_CLOSED') })
        }
      }
    },
    [load, t]
  )
  applyResultRef.current = applyResult

  /**
   * Single exit point for the popup flow.
   * outcome: 'connected' | 'error' | 'closed' (closed = user pressed Cancel / timed out)
   */
  const finish = useCallback(
    async (outcome, reason) => {
      if (!watchingRef.current) return // already finished (poll + message can race)
      watchingRef.current = false
      log('popup flow finished', { outcome, reason })
      stopPolling()
      closePopup()
      setWaiting(false)
      setBusy(false)
      await applyResultRef.current(outcome, reason)
    },
    [stopPolling, closePopup]
  )
  finishRef.current = finish

  const startWatching = useCallback(() => {
    watchingRef.current = true
    setWaiting(true)
    const startedAt = Date.now()
    log('waiting for result: listening for popup message + polling status every', POLL_MS, 'ms')

    pollRef.current = setInterval(async () => {
      if (!watchingRef.current) return

      if (Date.now() - startedAt > MAX_WAIT_MS) {
        log('gave up waiting (10 min)')
        finishRef.current('closed')
        return
      }

      try {
        // skipLoader: no global loading overlay for background polling
        const r = await apiGet(apiPath.pipedriveStatus, {}, { skipLoader: true })
        if (r?.status === 200 && r?.data?.data?.connected) {
          log('poll detected connected status')
          finishRef.current('connected')
        }
      } catch (e) {
        logErr('poll failed (will retry)', e?.response?.status || e?.message)
      }
    }, POLL_MS)
  }, [])

  // ---------- messages from the popup page (same origin) ----------
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined
    const ch = new BroadcastChannel(CHANNEL)
    ch.onmessage = (ev) => {
      const { result, reason } = ev.data || {}
      log('message from popup page', ev.data)
      if (result !== 'connected' && result !== 'error') return

      // 👇 CHANGED: instantly sync the sidebar state with the popup's outcome
      refreshPipedriveStatus()

      if (watchingRef.current) {
        finishRef.current(result, reason)
      } else {
        // flow was already cancelled here, but the popup finished anyway: still show the real result
        applyResultRef.current(result, reason)
      }
    }
    return () => ch.close()
  }, [refreshPipedriveStatus]) // 👈 CHANGED: added dependency

  // stop timers when leaving the page
  useEffect(
    () => () => {
      watchingRef.current = false
      stopPolling()
    },
    [stopPolling]
  )

  useEffect(() => {
    updatePageName(t('PIPEDRIVE_INTEGRATION'))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- runs on page load (also runs inside the popup after Pipedrive redirects back) ----------
  useEffect(() => {
    const result = params.get('pipedrive')
    const reason = params.get('reason')
    log('page mounted', { path: window.location.pathname, pipedriveParam: result, reason })

    if (result === 'connected') {
      setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
    } else if (result === 'error') {
      const key = ERROR_KEYS[reason]
      setBanner({ type: 'error', text: key ? t(key) : t('PIPEDRIVE_CONNECT_FAILED') })
    }

    if (result) {
      // 1) Tell the original Empivo tab how it went
      try {
        const ch = new BroadcastChannel(CHANNEL)
        ch.postMessage({ result, reason })
        ch.close()
        log('result sent to the original tab')
      } catch (e) {
        /* BroadcastChannel unsupported: the original tab's status polling still works */
      }
      navigate({ search: '' }, { replace: true })

      // 2) This page was reached from the Pipedrive popup: close this window.
      //    If the browser refuses, the window stays open and we show a hint instead.
      setTimeout(() => {
        log('trying to close this window')
        try {
          window.close()
        } catch (e) {
          /* ignore */
        }
        setTimeout(() => {
          log('window did not close, showing hint')
          setShowCloseHint(true)
        }, 700)
      }, 500)
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- actions ----------
  const handleConnect = async () => {
    if (busy || waiting) return
    setBusy(true)
    setBanner(null)

    // Open the popup NOW (synchronously inside the click) or browsers block it.
    const w = 620
    const h = 760
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2))
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2))
    const popup = window.open(
      '',
      POPUP_NAME,
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (!popup) {
      logErr('popup was blocked by the browser')
      setBanner({ type: 'error', text: t('PIPEDRIVE_POPUP_BLOCKED') })
      setBusy(false)
      return
    }
    popupRef.current = popup
    try {
      popup.document.title = 'Pipedrive'
      popup.document.body.innerHTML =
        '<p style="font-family:sans-serif;padding:24px">Connecting to Pipedrive…</p>'
    } catch (e) {
      /* ignore */
    }

    try {
      log('Connect clicked, requesting auth URL from', apiPath.pipedriveConnect)
      // Pass local origin so API redirects popup back here (not production login)
      const result = await apiGet(apiPath.pipedriveConnect, {
        frontend_origin: window.location.origin,
      })
      const authUrl = result?.data?.data?.authUrl
      log('connect response', {
        httpStatus: result?.status,
        success: result?.data?.success,
        gotAuthUrl: !!authUrl,
        authHost: authUrl ? new URL(authUrl).host : null, // full URL not logged (contains state)
        frontendOrigin: window.location.origin,
      })

      if (result?.status === 200 && result?.data?.success && authUrl) {
        // Keep opener so parent can close the popup after Connected
        popup.location.href = authUrl
        log('Pipedrive authorization opened in popup')
        setBusy(false)
        startWatching()
      } else {
        logErr('connect failed', result?.data)
        closePopup()
        setBanner({ type: 'error', text: result?.data?.message || t('PIPEDRIVE_START_FAILED') })
        setBusy(false)
      }
    } catch (e) {
      logErr('connect threw', e?.response?.status, e?.response?.data || e?.message)
      closePopup()
      setBanner({
        type: 'error',
        text: e?.response?.data?.message || t('PIPEDRIVE_START_FAILED'),
      })
      setBusy(false)
    }
  }

  const handleCancelWaiting = () => {
    log('user cancelled waiting')
    finish('closed')
  }

  const handleDisconnect = async () => {
    if (!window.confirm(t('PIPEDRIVE_DISCONNECT_CONFIRM'))) {
      log('disconnect cancelled by user')
      return
    }
    try {
      setBusy(true)
      log('disconnecting via', apiPath.pipedriveDisconnect)
      const result = await apiDelete(apiPath.pipedriveDisconnect)
      log('disconnect response', { httpStatus: result?.status, success: result?.data?.success })
      if (result?.status === 200 && result?.data?.success) {
        setBanner({ type: 'success', text: t('PIPEDRIVE_DISCONNECTED') })
        await load() // 👈 load() now also calls refreshPipedriveStatus → sidebar hides items
      } else {
        logErr('disconnect failed', result?.data)
        setBanner({
          type: 'error',
          text: result?.data?.message || t('PIPEDRIVE_DISCONNECT_FAILED'),
        })
      }
    } catch (e) {
      logErr('disconnect threw', e?.response?.status, e?.response?.data || e?.message)
      setBanner({
        type: 'error',
        text: e?.response?.data?.message || t('PIPEDRIVE_DISCONNECT_FAILED'),
      })
    } finally {
      setBusy(false)
    }
  }

  // ---------- render ----------
  const connected = status?.connected
  const hasError = status?.status === 'error'
  const fmt = (d) => (d ? new Date(d).toLocaleString() : '-')
  const bannerClass =
    banner?.type === 'success'
      ? 'bg-green-100 text-green-800'
      : banner?.type === 'info'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-red-100 text-red-800'

  return (
    <div className='bg-[#F9F9F9]'>
      <div className='px-3 py-4'>
        <div className='bg-white border border-[#E9EDF9] rounded-lg p-6 max-w-3xl'>
          <h2 className='text-xl font-semibold m-0'>{t('PIPEDRIVE_INTEGRATION')}</h2>
          <p className='text-gray-500 mt-1 mb-4'>{t('PIPEDRIVE_DESCRIPTION')}</p>

          {banner && (
            <div className={`relative mt-4 mb-4 px-4 py-3 rounded-lg ${bannerClass}`}>
              {banner.text}
              <button
                className='absolute right-3 top-2 text-lg bg-transparent border-none cursor-pointer'
                onClick={() => setBanner(null)}
                aria-label='Dismiss'
              >
                ×
              </button>
            </div>
          )}

          {showCloseHint && (
            <p className='text-sm text-gray-600 mb-4'>{t('PIPEDRIVE_CLOSE_WINDOW_HINT')}</p>
          )}

          <div className='border border-[#E9EDF9] rounded-lg p-5'>
            {loading && !waiting ? (
              <p>{t('LOADING')}…</p>
            ) : connected ? (
              <>
                <div className='flex items-center gap-2 mb-3'>
                  <span className='w-2.5 h-2.5 rounded-full bg-green-600 inline-block' />
                  <strong>{t('PIPEDRIVE_CONNECTED')}</strong>
                </div>
                <dl className='grid grid-cols-[180px_1fr] gap-y-2 mb-4'>
                  <dt>{t('PIPEDRIVE_COMPANY')}</dt>
                  <dd>{status.account?.companyName || '-'}</dd>
                  <dt>{t('PIPEDRIVE_DOMAIN')}</dt>
                  <dd>
                    {status.account?.companyDomain
                      ? `${status.account.companyDomain}.pipedrive.com`
                      : '-'}
                  </dd>
                  <dt>{t('PIPEDRIVE_AUTHORIZED_USER')}</dt>
                  <dd>
                    {status.account?.userName} ({status.account?.userEmail})
                  </dd>
                  <dt>{t('PIPEDRIVE_CONNECTED_ON')}</dt>
                  <dd>{fmt(status.connectedAt)}</dd>
                  <dt>{t('PIPEDRIVE_TOKEN_REFRESHED')}</dt>
                  <dd>{fmt(status.lastRefreshedAt)}</dd>
                </dl>
                <button
                  type='button'
                  disabled={busy}
                  onClick={handleDisconnect}
                  className='bg-red-100 text-red-700 font-semibold px-4 py-2 rounded-lg border border-transparent hover:bg-red-200 disabled:opacity-60'
                >
                  {busy ? t('WORKING') : t('PIPEDRIVE_DISCONNECT')}
                </button>
              </>
            ) : (
              <>
                <div className='flex items-center gap-2 mb-3'>
                  <span
                    className={`w-2.5 h-2.5 rounded-full inline-block ${
                      hasError ? 'bg-red-600' : 'bg-gray-400'
                    }`}
                  />
                  <strong>
                    {hasError
                      ? t('PIPEDRIVE_CONNECTION_PROBLEM')
                      : t('PIPEDRIVE_NOT_CONNECTED')}
                  </strong>
                </div>
                {hasError && (
                  <p className='text-red-700 mb-3'>
                    {status.lastError || t('PIPEDRIVE_NEEDS_REAUTH')}
                    {status.lastErrorAt ? ` (${fmt(status.lastErrorAt)})` : ''}
                  </p>
                )}
                <div className='flex items-center gap-4'>
                  <button
                    type='button'
                    disabled={busy || waiting}
                    onClick={handleConnect}
                    className='bg-LightBlue text-white font-semibold px-4 py-2 rounded-lg border border-transparent hover:bg-DarkBlue disabled:opacity-60'
                  >
                    {waiting
                      ? t('PIPEDRIVE_WAITING')
                      : busy
                      ? t('REDIRECTING')
                      : hasError
                      ? t('PIPEDRIVE_RECONNECT')
                      : t('PIPEDRIVE_CONNECT')}
                  </button>
                  {waiting && (
                    <button
                      type='button'
                      onClick={handleCancelWaiting}
                      className='text-sm text-gray-600 underline bg-transparent border-none cursor-pointer'
                    >
                      {t('CANCEL')}
                    </button>
                  )}
                </div>
                <p className='text-gray-500 text-sm mt-3'>
                  {waiting ? t('PIPEDRIVE_WAITING_HINT') : t('PIPEDRIVE_REDIRECT_NOTE')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}





// import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
// import { useNavigate, useSearchParams } from 'react-router-dom'
// import { useTranslation } from 'react-i18next'
// import AuthContext from 'context/AuthContext'
// import { apiGet, apiDelete } from '../../utils/apiFetch'
// import apiPath from '../../utils/pathObj'

// // Console tracing. Remove (or set to false) after testing.
// const DEBUG = true
// const log = (...args) => DEBUG && console.log('[Pipedrive UI]', ...args)
// const logErr = (...args) => DEBUG && console.error('[Pipedrive UI]', ...args)

// const CHANNEL = 'pipedrive_oauth' // same-origin tab-to-tab messaging
// const POPUP_NAME = 'pipedrive_oauth_popup'
// const POLL_MS = 2000
// const MAX_WAIT_MS = 10 * 60 * 1000 // matches the 10 minute `state` expiry on the server

// const ERROR_KEYS = {
//   access_denied: 'PIPEDRIVE_ERR_ACCESS_DENIED',
//   missing_params: 'PIPEDRIVE_ERR_MISSING_PARAMS',
//   invalid_state: 'PIPEDRIVE_ERR_INVALID_STATE',
//   exchange_failed: 'PIPEDRIVE_ERR_EXCHANGE_FAILED',
// }

// export default function PipedriveConnection() {
//   const { t } = useTranslation()
//   const navigate = useNavigate()
//   const { updatePageName } = useContext(AuthContext)
//   const [params] = useSearchParams()

//   const [status, setStatus] = useState(null)
//   const [loading, setLoading] = useState(true)
//   const [busy, setBusy] = useState(false)
//   const [waiting, setWaiting] = useState(false) // popup is open, waiting for the user
//   const [showCloseHint, setShowCloseHint] = useState(false) // popup page could not close itself
//   const [banner, setBanner] = useState(null) // { type: 'success' | 'error' | 'info', text }

//   const popupRef = useRef(null)
//   const pollRef = useRef(null)
//   const watchingRef = useRef(false)
//   const finishRef = useRef(() => {})
//   const applyResultRef = useRef(() => {})

//   // ---------- status ----------
//   const load = useCallback(async () => {
//     try {
//       setLoading(true)
//       log('loading status from', apiPath.pipedriveStatus)
//       const result = await apiGet(apiPath.pipedriveStatus)
//       log('status response', {
//         httpStatus: result?.status,
//         success: result?.data?.success,
//         data: result?.data?.data,
//       })
//       if (result?.status === 200 && result?.data?.success) {
//         setStatus(result.data.data)
//         return result.data.data
//       }
//       logErr('status response not in the expected shape', result)
//       setBanner({ type: 'error', text: t('PIPEDRIVE_LOAD_FAILED') })
//       return null
//     } catch (e) {
//       logErr('status request threw', e?.response?.status, e?.response?.data || e?.message)
//       setBanner({ type: 'error', text: t('PIPEDRIVE_LOAD_FAILED') })
//       return null
//     } finally {
//       setLoading(false)
//     }
//   }, [t])

//   // ---------- popup helpers ----------
//   const stopPolling = useCallback(() => {
//     if (pollRef.current) {
//       clearInterval(pollRef.current)
//       pollRef.current = null
//     }
//   }, [])

//   // NOTE: do not check popup.closed here. After the popup navigates to Pipedrive the browser
//   // reports it as "closed" even though it is still open, so we always just try to close it.
//   const closePopup = useCallback(() => {
//     const win = popupRef.current
//     popupRef.current = null
//     if (!win) return
//     const tryClose = () => {
//       try {
//         win.close()
//       } catch (e) {
//         /* ignore cross-origin */
//       }
//     }
//     tryClose()
//     // Retry — some browsers ignore the first close after cross-origin redirects
//     setTimeout(tryClose, 100)
//     setTimeout(tryClose, 400)
//     setTimeout(tryClose, 1000)
//   }, [])

//   // Shows the real outcome and refreshes the status from the server
//   const applyResult = useCallback(
//     async (outcome, reason) => {
//       if (outcome === 'connected') {
//         setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
//         await load()
//       } else if (outcome === 'error') {
//         const key = ERROR_KEYS[reason]
//         setBanner({ type: 'error', text: key ? t(key) : t('PIPEDRIVE_CONNECT_FAILED') })
//         await load()
//       } else {
//         // cancelled / timed out: check what actually happened on the server
//         const data = await load()
//         if (data?.connected) {
//           setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
//         } else if (data?.status === 'error') {
//           setBanner({ type: 'error', text: t('PIPEDRIVE_CONNECT_FAILED') })
//         } else {
//           setBanner({ type: 'info', text: t('PIPEDRIVE_POPUP_CLOSED') })
//         }
//       }
//     },
//     [load, t]
//   )
//   applyResultRef.current = applyResult

//   /**
//    * Single exit point for the popup flow.
//    * outcome: 'connected' | 'error' | 'closed' (closed = user pressed Cancel / timed out)
//    */
//   const finish = useCallback(
//     async (outcome, reason) => {
//       if (!watchingRef.current) return // already finished (poll + message can race)
//       watchingRef.current = false
//       log('popup flow finished', { outcome, reason })
//       stopPolling()
//       closePopup()
//       setWaiting(false)
//       setBusy(false)
//       await applyResultRef.current(outcome, reason)
//     },
//     [stopPolling, closePopup]
//   )
//   finishRef.current = finish

//   const startWatching = useCallback(() => {
//     watchingRef.current = true
//     setWaiting(true)
//     const startedAt = Date.now()
//     log('waiting for result: listening for popup message + polling status every', POLL_MS, 'ms')

//     pollRef.current = setInterval(async () => {
//       if (!watchingRef.current) return

//       if (Date.now() - startedAt > MAX_WAIT_MS) {
//         log('gave up waiting (10 min)')
//         finishRef.current('closed')
//         return
//       }

//       try {
//         // skipLoader: no global loading overlay for background polling
//         const r = await apiGet(apiPath.pipedriveStatus, {}, { skipLoader: true })
//         if (r?.status === 200 && r?.data?.data?.connected) {
//           log('poll detected connected status')
//           finishRef.current('connected')
//         }
//       } catch (e) {
//         logErr('poll failed (will retry)', e?.response?.status || e?.message)
//       }
//     }, POLL_MS)
//   }, [])

//   // ---------- messages from the popup page (same origin) ----------
//   useEffect(() => {
//     if (typeof BroadcastChannel === 'undefined') return undefined
//     const ch = new BroadcastChannel(CHANNEL)
//     ch.onmessage = (ev) => {
//       const { result, reason } = ev.data || {}
//       log('message from popup page', ev.data)
//       if (result !== 'connected' && result !== 'error') return
//       if (watchingRef.current) {
//         finishRef.current(result, reason)
//       } else {
//         // flow was already cancelled here, but the popup finished anyway: still show the real result
//         applyResultRef.current(result, reason)
//       }
//     }
//     return () => ch.close()
//   }, [])

//   // stop timers when leaving the page
//   useEffect(
//     () => () => {
//       watchingRef.current = false
//       stopPolling()
//     },
//     [stopPolling]
//   )

//   useEffect(() => {
//     updatePageName(t('PIPEDRIVE_INTEGRATION'))
//   }, []) // eslint-disable-line react-hooks/exhaustive-deps

//   // ---------- runs on page load (also runs inside the popup after Pipedrive redirects back) ----------
//   useEffect(() => {
//     const result = params.get('pipedrive')
//     const reason = params.get('reason')
//     log('page mounted', { path: window.location.pathname, pipedriveParam: result, reason })

//     if (result === 'connected') {
//       setBanner({ type: 'success', text: t('PIPEDRIVE_CONNECTED_SUCCESS') })
//     } else if (result === 'error') {
//       const key = ERROR_KEYS[reason]
//       setBanner({ type: 'error', text: key ? t(key) : t('PIPEDRIVE_CONNECT_FAILED') })
//     }

//     if (result) {
//       // 1) Tell the original Empivo tab how it went
//       try {
//         const ch = new BroadcastChannel(CHANNEL)
//         ch.postMessage({ result, reason })
//         ch.close()
//         log('result sent to the original tab')
//       } catch (e) {
//         /* BroadcastChannel unsupported: the original tab's status polling still works */
//       }
//       navigate({ search: '' }, { replace: true })

//       // 2) This page was reached from the Pipedrive popup: close this window.
//       //    If the browser refuses, the window stays open and we show a hint instead.
//       setTimeout(() => {
//         log('trying to close this window')
//         try {
//           window.close()
//         } catch (e) {
//           /* ignore */
//         }
//         setTimeout(() => {
//           log('window did not close, showing hint')
//           setShowCloseHint(true)
//         }, 700)
//       }, 500)
//     }
//     load()
//   }, []) // eslint-disable-line react-hooks/exhaustive-deps

//   // ---------- actions ----------
//   const handleConnect = async () => {
//     if (busy || waiting) return
//     setBusy(true)
//     setBanner(null)

//     // Open the popup NOW (synchronously inside the click) or browsers block it.
//     const w = 620
//     const h = 760
//     const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2))
//     const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2))
//     const popup = window.open(
//       '',
//       POPUP_NAME,
//       `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
//     )

//     if (!popup) {
//       logErr('popup was blocked by the browser')
//       setBanner({ type: 'error', text: t('PIPEDRIVE_POPUP_BLOCKED') })
//       setBusy(false)
//       return
//     }
//     popupRef.current = popup
//     try {
//       popup.document.title = 'Pipedrive'
//       popup.document.body.innerHTML =
//         '<p style="font-family:sans-serif;padding:24px">Connecting to Pipedrive…</p>'
//     } catch (e) {
//       /* ignore */
//     }

//     try {
//       log('Connect clicked, requesting auth URL from', apiPath.pipedriveConnect)
//       // Pass local origin so API redirects popup back here (not production login)
//       const result = await apiGet(apiPath.pipedriveConnect, {
//         frontend_origin: window.location.origin,
//       })
//       const authUrl = result?.data?.data?.authUrl
//       log('connect response', {
//         httpStatus: result?.status,
//         success: result?.data?.success,
//         gotAuthUrl: !!authUrl,
//         authHost: authUrl ? new URL(authUrl).host : null, // full URL not logged (contains state)
//         frontendOrigin: window.location.origin,
//       })

//       if (result?.status === 200 && result?.data?.success && authUrl) {
//         // Keep opener so parent can close the popup after Connected
//         popup.location.href = authUrl
//         log('Pipedrive authorization opened in popup')
//         setBusy(false)
//         startWatching()
//       } else {
//         logErr('connect failed', result?.data)
//         closePopup()
//         setBanner({ type: 'error', text: result?.data?.message || t('PIPEDRIVE_START_FAILED') })
//         setBusy(false)
//       }
//     } catch (e) {
//       logErr('connect threw', e?.response?.status, e?.response?.data || e?.message)
//       closePopup()
//       setBanner({
//         type: 'error',
//         text: e?.response?.data?.message || t('PIPEDRIVE_START_FAILED'),
//       })
//       setBusy(false)
//     }
//   }

//   const handleCancelWaiting = () => {
//     log('user cancelled waiting')
//     finish('closed')
//   }

//   const handleDisconnect = async () => {
//     if (!window.confirm(t('PIPEDRIVE_DISCONNECT_CONFIRM'))) {
//       log('disconnect cancelled by user')
//       return
//     }
//     try {
//       setBusy(true)
//       log('disconnecting via', apiPath.pipedriveDisconnect)
//       const result = await apiDelete(apiPath.pipedriveDisconnect)
//       log('disconnect response', { httpStatus: result?.status, success: result?.data?.success })
//       if (result?.status === 200 && result?.data?.success) {
//         setBanner({ type: 'success', text: t('PIPEDRIVE_DISCONNECTED') })
//         await load()
//       } else {
//         logErr('disconnect failed', result?.data)
//         setBanner({
//           type: 'error',
//           text: result?.data?.message || t('PIPEDRIVE_DISCONNECT_FAILED'),
//         })
//       }
//     } catch (e) {
//       logErr('disconnect threw', e?.response?.status, e?.response?.data || e?.message)
//       setBanner({
//         type: 'error',
//         text: e?.response?.data?.message || t('PIPEDRIVE_DISCONNECT_FAILED'),
//       })
//     } finally {
//       setBusy(false)
//     }
//   }

//   // ---------- render ----------
//   const connected = status?.connected
//   const hasError = status?.status === 'error'
//   const fmt = (d) => (d ? new Date(d).toLocaleString() : '-')
//   const bannerClass =
//     banner?.type === 'success'
//       ? 'bg-green-100 text-green-800'
//       : banner?.type === 'info'
//       ? 'bg-blue-100 text-blue-800'
//       : 'bg-red-100 text-red-800'

//   return (
//     <div className='bg-[#F9F9F9]'>
//       <div className='px-3 py-4'>
//         <div className='bg-white border border-[#E9EDF9] rounded-lg p-6 max-w-3xl'>
//           <h2 className='text-xl font-semibold m-0'>{t('PIPEDRIVE_INTEGRATION')}</h2>
//           <p className='text-gray-500 mt-1 mb-4'>{t('PIPEDRIVE_DESCRIPTION')}</p>

//           {banner && (
//             <div className={`relative mt-4 mb-4 px-4 py-3 rounded-lg ${bannerClass}`}>
//               {banner.text}
//               <button
//                 className='absolute right-3 top-2 text-lg bg-transparent border-none cursor-pointer'
//                 onClick={() => setBanner(null)}
//                 aria-label='Dismiss'
//               >
//                 ×
//               </button>
//             </div>
//           )}

//           {showCloseHint && (
//             <p className='text-sm text-gray-600 mb-4'>{t('PIPEDRIVE_CLOSE_WINDOW_HINT')}</p>
//           )}

//           <div className='border border-[#E9EDF9] rounded-lg p-5'>
//             {loading && !waiting ? (
//               <p>{t('LOADING')}…</p>
//             ) : connected ? (
//               <>
//                 <div className='flex items-center gap-2 mb-3'>
//                   <span className='w-2.5 h-2.5 rounded-full bg-green-600 inline-block' />
//                   <strong>{t('PIPEDRIVE_CONNECTED')}</strong>
//                 </div>
//                 <dl className='grid grid-cols-[180px_1fr] gap-y-2 mb-4'>
//                   <dt>{t('PIPEDRIVE_COMPANY')}</dt>
//                   <dd>{status.account?.companyName || '-'}</dd>
//                   <dt>{t('PIPEDRIVE_DOMAIN')}</dt>
//                   <dd>
//                     {status.account?.companyDomain
//                       ? `${status.account.companyDomain}.pipedrive.com`
//                       : '-'}
//                   </dd>
//                   <dt>{t('PIPEDRIVE_AUTHORIZED_USER')}</dt>
//                   <dd>
//                     {status.account?.userName} ({status.account?.userEmail})
//                   </dd>
//                   <dt>{t('PIPEDRIVE_CONNECTED_ON')}</dt>
//                   <dd>{fmt(status.connectedAt)}</dd>
//                   <dt>{t('PIPEDRIVE_TOKEN_REFRESHED')}</dt>
//                   <dd>{fmt(status.lastRefreshedAt)}</dd>
//                 </dl>
//                 <button
//                   type='button'
//                   disabled={busy}
//                   onClick={handleDisconnect}
//                   className='bg-red-100 text-red-700 font-semibold px-4 py-2 rounded-lg border border-transparent hover:bg-red-200 disabled:opacity-60'
//                 >
//                   {busy ? t('WORKING') : t('PIPEDRIVE_DISCONNECT')}
//                 </button>
//               </>
//             ) : (
//               <>
//                 <div className='flex items-center gap-2 mb-3'>
//                   <span
//                     className={`w-2.5 h-2.5 rounded-full inline-block ${
//                       hasError ? 'bg-red-600' : 'bg-gray-400'
//                     }`}
//                   />
//                   <strong>
//                     {hasError
//                       ? t('PIPEDRIVE_CONNECTION_PROBLEM')
//                       : t('PIPEDRIVE_NOT_CONNECTED')}
//                   </strong>
//                 </div>
//                 {hasError && (
//                   <p className='text-red-700 mb-3'>
//                     {status.lastError || t('PIPEDRIVE_NEEDS_REAUTH')}
//                     {status.lastErrorAt ? ` (${fmt(status.lastErrorAt)})` : ''}
//                   </p>
//                 )}
//                 <div className='flex items-center gap-4'>
//                   <button
//                     type='button'
//                     disabled={busy || waiting}
//                     onClick={handleConnect}
//                     className='bg-LightBlue text-white font-semibold px-4 py-2 rounded-lg border border-transparent hover:bg-DarkBlue disabled:opacity-60'
//                   >
//                     {waiting
//                       ? t('PIPEDRIVE_WAITING')
//                       : busy
//                       ? t('REDIRECTING')
//                       : hasError
//                       ? t('PIPEDRIVE_RECONNECT')
//                       : t('PIPEDRIVE_CONNECT')}
//                   </button>
//                   {waiting && (
//                     <button
//                       type='button'
//                       onClick={handleCancelWaiting}
//                       className='text-sm text-gray-600 underline bg-transparent border-none cursor-pointer'
//                     >
//                       {t('CANCEL')}
//                     </button>
//                   )}
//                 </div>
//                 <p className='text-gray-500 text-sm mt-3'>
//                   {waiting ? t('PIPEDRIVE_WAITING_HINT') : t('PIPEDRIVE_REDIRECT_NOTE')}
//                 </p>
//               </>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   )
// }